import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateOrderInput,
  Order,
  OrderListQuery,
  OrderListResult,
} from '@faktorplus/shared';
import { apiRequest } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

/**
 * How often the history asks again while something is still on its way out.
 * The worker polls the outbox every few seconds, so an order rarely stays
 * pending longer than one or two of these.
 */
const PENDING_REFETCH_MS = 3_000;

export function useOrders(query: Partial<OrderListQuery> = {}) {
  return useQuery({
    queryKey: queryKeys.orders.list(query),
    queryFn: () => apiRequest<OrderListResult>('/orders', { query }),
    placeholderData: (previous) => previous,
    // Sending happens after the request that placed the order, so the status
    // on screen has to catch up on its own — otherwise "Wysyłanie" would sit
    // there until the manager thought to refresh.
    refetchInterval: ({ state }) => (hasPending(state.data) ? PENDING_REFETCH_MS : false),
  });
}

export function useSendOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateOrderInput) =>
      apiRequest<Order>('/orders', { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
  });
}

export function useResendOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiRequest<Order>(`/orders/${id}/resend`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
  });
}

function hasPending(result: OrderListResult | undefined): boolean {
  return result?.items.some((order) => order.status === 'PENDING') ?? false;
}
